import React, { useState, useRef, useEffect } from 'react';
import { PleriClient } from '../../client/PleriClient';
import { MagnifyingGlassIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { LibraryItem, libraryItems, libraryGroups } from './libraryData';
import { useTranslation } from 'react-i18next';

export interface LibraryContentProps {
  onClose?: () => void;
}

interface LibraryCardProps {
  item: LibraryItem;
  onClick: (item: LibraryItem) => void;
}

const LibraryCard: React.FC<LibraryCardProps> = ({ item, onClick }) => {
  const { t } = useTranslation('pleri/library');
  return (
    <button
      onClick={() => onClick(item)}
      data-testid="quickstart-item"
      className="group relative flex h-full w-full flex-col rounded-2xl border border-gray-100 bg-white p-5 text-left shadow-sm transition-all duration-150 hover:border-gray-200 hover:shadow-md dark:border-gray-800 dark:bg-gray-900"
    >
      <div className="flex items-start justify-between">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gray-50 text-gray-700 group-hover:bg-orange-50 group-hover:text-orange-600 dark:bg-gray-800 dark:text-gray-300">
          {item.icon}
        </div>
        {!item.autoSubmit && (
          <span className="flex-shrink-0 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-950">
            {t('template')}
          </span>
        )}
      </div>
      <div className="mt-4 flex-1">
        <h3 className="line-clamp-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          {item.title}
        </h3>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          {item.description}
        </p>
      </div>
      <div className="mt-4 flex flex-nowrap gap-1 overflow-x-auto">
        {item.tags.slice(0, 3).map((tag) => (
          <span
            key={tag}
            className="inline-flex flex-shrink-0 items-center rounded-md bg-gray-50 px-1.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400"
          >
            {tag}
          </span>
        ))}
      </div>
    </button>
  );
};

export const LibraryContent: React.FC<LibraryContentProps> = ({ onClose }) => {
  const { t } = useTranslation('pleri/library');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeSection, setActiveSection] = useState<string>('featured');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const featuredItems = libraryItems(t).filter((item) => item.featured);

  const handleItemClick = (item: LibraryItem) => {
    PleriClient.updateChatInput({
      prompt: item.prompt,
      autoSubmit: item.autoSubmit,
      metrics: {
        eventLocation: 'library',
        eventName: 'libraryItemClick',
        eventType: item.autoSubmit ? 'autoSubmitQuery' : 'templateUse',
        eventValue: item.id,
        eventDetails: {
          category: item.category,
          title: item.title,
          tags: item.tags,
        },
      },
    });

    if (onClose) {
      onClose();
    }
  };

  // Intersection Observer to track active section
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }

    const observerOptions = {
      root: scrollContainer,
      rootMargin: '-20% 0px -70% 0px',
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id.replace('section-', '');
          setActiveSection(sectionId);
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions,
    );

    // Observe all sections
    const categories = [
      {
        id: 'featured',
        name: t('featured'),
        description: t('curatedSecurityToolsAndTemplatesToGet'),
        items: featuredItems,
      },
      ...libraryGroups(t),
    ];

    categories.forEach((category) => {
      const element = document.getElementById(`section-${category.id}`);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [featuredItems, t]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(`section-${sectionId}`);
    const scrollContainer = scrollContainerRef.current;

    if (element && scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const offset = 80; // Account for sticky header
      const scrollTop =
        scrollContainer.scrollTop +
        elementRect.top -
        containerRect.top -
        offset;

      scrollContainer.scrollTo({
        top: scrollTop,
        behavior: 'smooth',
      });
    }
  };

  const filterItems = (items: LibraryItem[]) => {
    if (!searchQuery) {
      return items;
    }
    return items.filter(
      (item) =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.tags.some((tag) =>
          tag.toLowerCase().includes(searchQuery.toLowerCase()),
        ),
    );
  };

  const categories = [
    {
      id: 'featured',
      name: t('featured'),
      description: t('curatedSecurityToolsAndTemplatesToGet'),
      items: featuredItems,
    },
    ...libraryGroups(t),
  ];

  return (
    <div
      className="flex h-full w-full flex-col overflow-y-auto bg-white dark:bg-gray-900"
      ref={scrollContainerRef}
    >
      {/* Header with Search */}
      <div className="border-b border-gray-100 px-6 py-8 dark:border-gray-800">
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-2xl leading-tight font-semibold tracking-tight text-gray-900 dark:text-gray-100">
            {t('quickstart')}
          </h1>
          <p className="mt-2 text-base text-gray-600 dark:text-gray-400">
            {t('discoverAndExplorePleriSecurityCapabilities')}
          </p>
        </div>

        {/* Search Bar */}
        <div className="mx-auto mt-6 max-w-md">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder={t('searchPromptsAndTemplates')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pr-4 pl-10 text-sm focus:border-orange-300 focus:ring-2 focus:ring-orange-200 focus:outline-none dark:border-gray-700 dark:bg-gray-900"
            />
          </div>
        </div>
      </div>
      {/* Sticky Navigation */}
      <div className="sticky top-0 z-10 border-b border-gray-100 bg-white/95 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/95">
        <div className="mx-auto max-w-7xl px-6">
          <nav
            className="flex justify-center space-x-8 overflow-x-auto py-2"
            aria-label={t('librarySections')}
          >
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => scrollToSection(category.id)}
                data-testid="quickstart-category"
                className={`border-b-2 px-1 py-1 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeSection === category.id
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
                }`}
              >
                {category.name}
              </button>
            ))}
          </nav>
        </div>
      </div>
      <div className="flex-1">
        <div className="mx-auto max-w-7xl px-6 py-8">
          {categories.map((category) => {
            const filteredItems = filterItems(category.items);

            // Skip section if no items after filtering
            if (searchQuery && filteredItems.length === 0) {
              return null;
            }

            return (
              <section
                key={category.id}
                id={`section-${category.id}`}
                className="mb-16 last:mb-8"
              >
                <div className="mb-8">
                  <h2 className="text-xl leading-tight font-semibold text-gray-900 dark:text-gray-100">
                    {category.name}
                  </h2>
                  <p className="mt-2 text-base text-gray-600 dark:text-gray-400">
                    {category.description}
                  </p>
                  {searchQuery && (
                    <p className="mt-1 text-sm text-orange-600">
                      {t('resultsFound', { count: filteredItems.length })}
                    </p>
                  )}
                </div>
                {filteredItems.length > 0 ? (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {filteredItems.map((item) => (
                      <LibraryCard
                        key={item.id}
                        item={item}
                        onClick={handleItemClick}
                      />
                    ))}
                  </div>
                ) : (
                  !searchQuery && (
                    <div className="flex h-32 items-center justify-center rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700">
                      <div className="text-center">
                        <SparklesIcon className="mx-auto h-8 w-8 text-gray-300" />
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                          {t('comingSoon')}
                        </p>
                      </div>
                    </div>
                  )
                )}
              </section>
            );
          })}

          {/* No search results */}
          {searchQuery &&
            categories.every((cat) => filterItems(cat.items).length === 0) && (
              <div className="flex h-64 items-center justify-center">
                <div className="text-center">
                  <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-300" />
                  <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('noResultsFound')}
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    {t('tryAdjustingYourSearchTermsOrBrowse')}
                  </p>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};
